import { PageLayout } from '@app/pages/layout/PageLayout'
import { RegionalPageLayout } from '@app/pages/layout/RegionalPageLayout'
import { useQuery } from '@based/sdk/react'
import { useMemo } from 'react'
import { useLocation, useSearch } from 'wouter'
import { KnowledgeHubModule } from '@schema/knowledgeHubModule'
import { LeftSidebar } from '@app/components/layout/LeftSidebar'
import { KnowledgeHubModuleSidebar } from './KnowledgeHubModuleSidebar'
import { KnowledgeHubModuleContent } from './KnowledgeHubModuleContent'
import { useTranslations } from '@i18n/useLanguage'
import { pages } from '@app/util/links'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import { styled } from 'inlines'
import { Text } from '@app/components/core/typography/Text'
import {
  linkStyle,
  linkSizeStyle,
  linkIconSizeStyle,
} from '@app/components/core/link/linkStyleHeper'
import { getEntityPageStatus } from '@app/util/getEntityPageStatus'
import { NotFoundPage } from '@app/pages/error/NotFoundPage'
import { PageSeo } from '@app/components/core/seo/PageSeo'
import { RegionViewModel } from 'functions/app/geography/view/region'
import { regionsTabs } from '@app/pages/discover/regions/constants'

const BackLink = styled.a

export type KnowledgeHubItemPageProps = {
  itemId: string
  regionSlug?: string
}

const KnowledgeHubModuleBody = ({
  backPath,
  onBackClick,
  backLabel,
  menuLabel,
  sections,
  itemSlug,
  moduleTitle,
}: {
  backPath: string
  onBackClick: (e: React.MouseEvent<HTMLAnchorElement>) => void
  backLabel: string
  menuLabel: string
  sections: KnowledgeHubModule['sections']
  itemSlug?: string
  moduleTitle: string
}) => (
  <LeftSidebar>
    <LeftSidebar.Sidebar collapsedButtonLabel={menuLabel}>
      <KnowledgeHubModuleSidebar sections={sections} activeSlug={itemSlug} />
    </LeftSidebar.Sidebar>
    <div>
      <BackLink href={backPath} onClick={onBackClick} style={linkStyle}>
        <FontAwesomeIcon icon={faArrowLeft} style={linkIconSizeStyle.medium} />
        <Text.Medium style={linkSizeStyle.medium}>{backLabel}</Text.Medium>
      </BackLink>
      <KnowledgeHubModuleContent
        id={itemSlug || ''}
        moduleTitle={moduleTitle}
      />
    </div>
  </LeftSidebar>
)

export const KnowledgeHubModulePage = ({
  itemId,
  regionSlug,
}: KnowledgeHubItemPageProps) => {
  const t = useTranslations().app.knowledgeHubPage
  const [, setLocation] = useLocation()
  const regionQuery = useQuery(
    regionSlug ? 'view-region' : null,
    regionSlug ? { slug: regionSlug } : undefined,
  )
  const region: RegionViewModel | undefined = regionQuery.data
  const knowledgeHubModuleQuery = useQuery('view-knowledge-hub-module', {
    id: itemId,
  })
  const knowledgeHubModuleItem: KnowledgeHubModule | undefined =
    knowledgeHubModuleQuery.data
  const search = useSearch()
  const itemSlug = useMemo(
    () =>
      new URLSearchParams(search).get('slug') ||
      knowledgeHubModuleItem?.sections?.[0]?.knowledgeHubItems?.[0]?.slug,
    [search, knowledgeHubModuleItem],
  )
  const itemQuery = useQuery(
    itemSlug ? 'view-knowledge-hub-item' : null,
    itemSlug ? { id: itemSlug } : undefined,
  )

  const backPath = regionSlug
    ? pages.regionPageNew(regionSlug, regionsTabs.knowledgeHub)
    : pages.knowledgeHub

  const handleBackClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    if (window.history.length > 1) {
      window.history.back()
    } else {
      setLocation(backPath)
    }
  }

  const modulePageStatus = getEntityPageStatus(
    knowledgeHubModuleQuery.loading,
    knowledgeHubModuleItem,
  )
  const regionPageStatus = regionSlug
    ? getEntityPageStatus(regionQuery.loading, region)
    : null

  if (
    modulePageStatus === 'not-found' ||
    regionPageStatus === 'not-found'
  ) {
    return <NotFoundPage />
  }

  if (
    modulePageStatus === 'loading' ||
    regionPageStatus === 'loading'
  ) {
    return (
      <PageLayout gap="2rem">
        <PageSeo />
        {t.loading}
      </PageLayout>
    )
  }

  if (itemSlug) {
    const itemPageStatus = getEntityPageStatus(itemQuery.loading, itemQuery.data)
    if (itemPageStatus === 'not-found') {
      return <NotFoundPage />
    }
    if (itemPageStatus === 'loading') {
      return (
        <PageLayout gap="2rem">
          <PageSeo />
          {t.loading}
        </PageLayout>
      )
    }
  }

  const body = (
    <KnowledgeHubModuleBody
      backPath={backPath}
      onBackClick={handleBackClick}
      backLabel={t.back}
      menuLabel={t.menu}
      sections={knowledgeHubModuleItem.sections}
      itemSlug={itemSlug}
      moduleTitle={knowledgeHubModuleItem.title || ''}
    />
  )

  if (regionSlug && region) {
    return (
      <RegionalPageLayout
        region={region}
        tab={regionsTabs.knowledgeHub}
        gap="0rem"
      >
        <PageSeo />
        {body}
      </RegionalPageLayout>
    )
  }

  return (
    <PageLayout gap="0rem">
      <PageSeo />
      {body}
    </PageLayout>
  )
}
